import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import adminService from "../../api/services/adminService";
import { buildSubscriptionHistoryParams } from "./buildParams";
import { getFilterLabel as getFilterLabelUtil } from "./getFilterLabel";
import { INITIAL_FILTERS, LIMIT, STATUS_OPTIONS, PAYMENT_METHOD_OPTIONS, RECORD_STATE_OPTIONS } from "./constants";

/**
 * Merge record-type and email-type filters into API params.
 * recordType (Weekly/Monthly) sets subscription to matching plan id; emailType is passed through from filters.
 */
function mergeTabFilters(filters, plans) {
  const merged = { ...filters };
  if (filters.recordType === "Weekly" && plans && plans.length) {
    const weekly = plans.find((p) => (p.duration || "").toLowerCase().includes("week") || (p.name || "").toLowerCase().includes("week"));
    if (weekly) merged.subscription = String(weekly.id);
  } else if (filters.recordType === "Monthly" && plans && plans.length) {
    const monthly = plans.find((p) => (p.duration || "").toLowerCase().includes("month") && !(p.name || "").toLowerCase().includes("3 month"));
    if (monthly) merged.subscription = String(monthly.id);
    if (!merged.subscription) {
      const fallback = plans.find((p) => (p.name || "").toLowerCase().includes("month"));
      if (fallback) merged.subscription = String(fallback.id);
    }
  }
  return merged;
}

/**
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - If false, skips fetching.
 */
export function useSubscriptionHistory(options = {}) {
  const { enabled = true } = options;
  const [historyFilters, setHistoryFilters] = useState(INITIAL_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [plans, setPlans] = useState([]);
  const [manualStats, setManualStats] = useState({ active: 0, expired: 0, canceled: 0 });
  const [revenueCatStats, setRevenueCatStats] = useState({ active: 0, expired: 0, canceled: 0 });
  const plansRef = useRef([]);
  const hasFetchedPlans = useRef(false);
  plansRef.current = plans;

  useEffect(() => {
    if (hasFetchedPlans.current) return;
    hasFetchedPlans.current = true;
    adminService
      .getSubscriptionPlans()
      .then((res) => {
        if (res?.success && res?.plans) setPlans(res.plans);
      })
      .catch(() => {});
  }, []);

  const fetchSubscriptionHistory = useCallback(async (page = 1, filters = {}, signal = null) => {
    setLoadingHistory(true);
    setHistoryError("");
    try {
      const merged = mergeTabFilters(filters, plansRef.current);
      const params = buildSubscriptionHistoryParams(merged, page, LIMIT);
      const response = await adminService.getAllSubscriptions(params, signal);
      if (response?.success && response?.subscriptions) {
        setSubscriptionHistory(response.subscriptions);
        if (response.pagination) {
          setCurrentPage(response.pagination.currentPage);
          setTotalPages(response.pagination.totalPages);
          setTotalItems(response.pagination.totalItems);
        }
        if (response.manualStats && typeof response.manualStats === "object") {
          setManualStats({
            active: response.manualStats.active ?? 0,
            expired: response.manualStats.expired ?? 0,
            canceled: response.manualStats.canceled ?? 0,
          });
        }
        if (response.revenueCatStats && typeof response.revenueCatStats === "object") {
          setRevenueCatStats({
            active: response.revenueCatStats.active ?? 0,
            expired: response.revenueCatStats.expired ?? 0,
            canceled: response.revenueCatStats.canceled ?? 0,
          });
        }
      }
    } catch (err) {
      if (err?.name === "AbortError" || err?.code === "ERR_CANCELED") return;
      console.error("Error fetching subscription records:", err);
      setHistoryError(err?.message || "Failed to fetch subscription records");
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const merged = mergeTabFilters(historyFilters, plansRef.current);
    fetchSubscriptionHistory(currentPage, merged, controller.signal);
    return () => controller.abort();
  }, [
    enabled,
    currentPage,
    historyFilters.user_id,
    historyFilters.name,
    historyFilters.email,
    historyFilters.contactNo,
    historyFilters.date_from,
    historyFilters.date_to,
    historyFilters.subscription,
    historyFilters.source,
    historyFilters.record_state,
    historyFilters.currency,
    historyFilters.status,
    historyFilters.emailType,
    historyFilters.payment_method,
    fetchSubscriptionHistory,
  ]);

  const handleFilterChange = useCallback((key, value) => {
    setHistoryFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setHistoryFilters(INITIAL_FILTERS);
    setCurrentPage(1);
  }, []);

  const removeFilter = useCallback((key) => {
    setHistoryFilters((prev) => ({ ...prev, [key]: "" }));
    setCurrentPage(1);
  }, []);

  const getFilterLabel = useCallback(
    (key, value) => getFilterLabelUtil(key, value),
    []
  );

  const getActiveFiltersCount = useCallback(() => {
    return Object.values(historyFilters).filter(
      (v) => v !== "" && v != null
    ).length;
  }, [historyFilters]);

  const activeFilterEntries = useMemo(
    () =>
      Object.keys(historyFilters).filter(
        (key) => historyFilters[key] !== "" && historyFilters[key] != null
      ),
    [historyFilters]
  );

  const primaryFields = useMemo(
    () => [
      {
        name: "user_id",
        label: "User ID",
        type: "number",
        placeholder: "Filter by user ID",
      },
      {
        name: "name",
        label: "Name",
        type: "text",
        placeholder: "Search by name",
      },
      {
        name: "email",
        label: "Email",
        type: "text",
        placeholder: "Search by email",
      },
      {
        name: "contactNo",
        label: "Contact No",
        type: "text",
        placeholder: "Search by contact",
      },
      { name: "date_from", label: "Date From", type: "date" },
      { name: "date_to", label: "Date To", type: "date" },
      {
        name: "subscription",
        label: "Subscription Type",
        type: "select",
        options: [
          { value: "", name: "All Types" },
          ...(plans || []).map((p) => ({
            value: String(p.id),
            name: p.name || `Plan ${p.id}`,
          })),
        ],
      },
      {
        name: "source",
        label: "Source",
        type: "select",
        options: [
          { value: "", name: "All Sources" },
          { value: "manual", name: "Manual" },
          { value: "revenuecat", name: "RevenueCat" },
        ],
      },
      {
        name: "record_state",
        label: "Record State",
        type: "select",
        options: RECORD_STATE_OPTIONS,
      },
      {
        name: "status",
        label: "Status",
        type: "select",
        options: STATUS_OPTIONS,
      },
      {
        name: "currency",
        label: "Currency",
        type: "text",
        placeholder: "e.g. PKR, USD",
      },
      {
        name: "emailType",
        label: "User Email Type",
        type: "select",
        options: [
          { value: "", name: "All" },
          { value: "naseebimail", name: "Naseebi Users" },
          { value: "other", name: "Other Users" },
        ],
      },
      {
        name: "payment_method",
        label: "Payment Method",
        type: "select",
        options: PAYMENT_METHOD_OPTIONS,
      },
    ],
    [plans]
  );

  return {
    historyFilters,
    setHistoryFilters,
    handleFilterChange,
    clearFilters,
    removeFilter,
    getFilterLabel,
    getActiveFiltersCount,
    activeFilterEntries,
    primaryFields,
    subscriptionHistory,
    currentPage,
    setCurrentPage,
    totalPages,
    totalItems,
    manualStats,
    revenueCatStats,
    loadingHistory,
    historyError,
    limit: LIMIT,
    fetchSubscriptionHistory,
  };
}
